import React from 'react';
import { getIntl } from '../../../../utils/intl';
import ListSection, { MAX_CARD_COUNT } from './ListSection';
import { LoadingContent } from '../../Loader';
import Stack, { contentStackSpacing } from '../../Stack';
import { useProfilesTabNavigation } from '../../../../utils/appNavigationNames';
import { useMaybeRenderedSideDrawerToggle } from '../../SideDrawer/MaybeRenderedSideDrawer';
import SendMessageContent from './SendMessageContent';
import { useProfileInAppNotifications } from '../../../../hooks/api/useProfileInAppNotifications';
import InAppNotificationCard, { useNavigateOnInAppNotificationPress } from '../../../shared/InAppNotificationCard';
import MessageSideDrawerContent from './MessageSideDrawerContent';

const intl = getIntl(
    'components.screens.tabs.profiles.profileActivityScreen.desktopRecentActivitySection.messagesList',
    {
        noMessages: 'No Profile Messages',
        messages: 'Messages',
        recentMessages: 'Recent Messages',
        profileMessages: 'Profile Messages',
        sendMessage: 'Send Message',
        sendProfileMessage: 'Send Profile Message',
    },
);

export type MessagesListOwnProps = {
    profileId: string;
};

export type MessagesListProps = MessagesListOwnProps;

const MessagesList: React.FC<MessagesListProps> = (props) => {
    const { navigate } = useProfilesTabNavigation();
    const { isLoadingNotifications, allNotifications, toggleReadStatus } = useProfileInAppNotifications({
        medicalProfileId: props.profileId,
    });
    const navigateOnInAppNotificationOnPress = useNavigateOnInAppNotificationPress();

    const messageListDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'profileMessages',
        title: intl('profileMessages'),
        renderContent: (onClose) => (
            <MessageSideDrawerContent
                onClose={() => onClose(true)}
                medicalProfileId={props.profileId}
                onMessageProfile={() => {
                    onClose(true);
                    navigate('message-profile', {
                        profileId: props.profileId,
                    });
                }}
            />
        ),
        mobileOnFocus: () => {
            // no-op - shouldn't be rendered on mobile
            return;
        },
    });

    const addMessageDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addProfileMessages',
        title: intl('sendProfileMessage'),
        renderContent: (onClose) => <SendMessageContent profileId={props.profileId} onClose={onClose} />,
        mobileOnFocus: () => {
            // no-op - shouldn't be rendered on mobile
            return;
        },
    });

    return (
        <>
            <ListSection
                children={
                    isLoadingNotifications ? (
                        <LoadingContent />
                    ) : (
                        <Stack vertical space={contentStackSpacing}>
                            {allNotifications.slice(0, MAX_CARD_COUNT).map((notification) => {
                                return (
                                    <InAppNotificationCard
                                        key={JSON.stringify(notification)}
                                        inAppNotification={notification}
                                        medicalProfileId={props.profileId}
                                        onPress={(medicalProfileId, inAppNotification) => {
                                            navigateOnInAppNotificationOnPress(medicalProfileId, inAppNotification);
                                            if (!inAppNotification.read) {
                                                toggleReadStatus({
                                                    notificationsToToggle: [inAppNotification],
                                                    preventSuccessNotification: true,
                                                });
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    )
                }
                title={intl('recentMessages')}
                icon={'Mail'}
                onPressSeeAll={messageListDetailsSideDrawer.onOpen}
                noResultsMessage={intl('noMessages')}
                showNoResultsMessage={!allNotifications.length && !isLoadingNotifications}
                seeAllTestId="see-all-messages"
                onAdd={addMessageDetailsSideDrawer.onOpen}
                addTestId="add-message-from-list"
            />
            {messageListDetailsSideDrawer.SideDrawer}
            {addMessageDetailsSideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(MessagesList);
